import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import ImageField from "@/pages/admin/ImageField";
import FeatureToggles from "@/pages/admin/FeatureToggles";

// Field definitions per section (label + field key + type)
const SECTIONS = [
  {
    id: "brand",
    title: "Varumärke",
    description: "Logotyp och namn som visas i header & footer. Ladda upp en bild för att ersätta texten.",
    fields: [
      { key: "logo_url", label: "Logotyp (rund bild fungerar bäst)", type: "image" },
      { key: "name", label: "Restaurangens namn", type: "text" },
      { key: "initial", label: "Logotyp-initial (fallback om ingen bild)", type: "text" },
    ],
  },
  {
    id: "hero",
    title: "Hero (startsektionen)",
    description: "Första intrycket högst upp på sidan.",
    fields: [
      { key: "eyebrow", label: "Överrubrik (små versaler)", type: "text" },
      { key: "title_line1", label: "Huvudrubrik rad 1", type: "text" },
      { key: "title_italic", label: "Huvudrubrik rad 2 (kursiv)", type: "text" },
      { key: "subtitle", label: "Undertext", type: "textarea" },
      { key: "image_url", label: "Hero-bild", type: "image" },
      { key: "cta_primary", label: "Knapp 1 (primär)", type: "text" },
      { key: "cta_secondary", label: "Knapp 2 (sekundär)", type: "text" },
      { key: "cta_tertiary", label: "Knapp 3 (länk)", type: "text" },
      { key: "badge_label", label: "Prisbadge text (vänster)", type: "text" },
      { key: "badge_price", label: "Prisbadge pris", type: "text" },
      { key: "info_1", label: "Info-ruta 1", type: "text" },
      { key: "info_2", label: "Info-ruta 2", type: "text" },
      { key: "info_3", label: "Info-ruta 3", type: "text" },
      { key: "info_4", label: "Info-ruta 4", type: "text" },
      { key: "rating_value", label: "Betyg (siffra)", type: "text" },
      { key: "rating_label", label: "Betyg rubrik", type: "text" },
      { key: "rating_count", label: "Antal recensioner text", type: "text" },
    ],
  },
  {
    id: "about",
    title: "Om oss",
    description: "Beskrivning av restaurangen.",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title_line1", label: "Rubrik rad 1", type: "text" },
      { key: "title_italic", label: "Rubrik rad 2 (kursiv)", type: "text" },
      { key: "paragraph_1", label: "Text 1", type: "textarea" },
      { key: "paragraph_2", label: "Text 2", type: "textarea" },
      { key: "image_url", label: "Bild (interiör)", type: "image" },
      { key: "stat_1_value", label: "Statistik 1 – värde", type: "text" },
      { key: "stat_1_label", label: "Statistik 1 – etikett", type: "text" },
      { key: "stat_2_value", label: "Statistik 2 – värde", type: "text" },
      { key: "stat_2_label", label: "Statistik 2 – etikett", type: "text" },
      { key: "stat_3_value", label: "Statistik 3 – värde", type: "text" },
      { key: "stat_3_label", label: "Statistik 3 – etikett", type: "text" },
    ],
  },
  {
    id: "menu_section",
    title: "Menyrubrik",
    description: "Rubrik och CTA över menyn.",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title_line1", label: "Rubrik rad 1", type: "text" },
      { key: "title_italic", label: "Rubrik rad 2 (kursiv)", type: "text" },
      { key: "cta_text", label: "CTA-text under menyn", type: "text" },
    ],
  },
  {
    id: "reviews",
    title: "Recensioner",
    description: "Tre utvalda gästrecensioner.",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title", label: "Rubrik", type: "text" },
      { key: "rating_value", label: "Snittbetyg", type: "text" },
      { key: "rating_count", label: "Antal recensioner (text)", type: "text" },
      { key: "review_1_name", label: "Recension 1 – namn", type: "text" },
      { key: "review_1_rating", label: "Recension 1 – stjärnor (1-5)", type: "number" },
      { key: "review_1_text", label: "Recension 1 – text", type: "textarea" },
      { key: "review_2_name", label: "Recension 2 – namn", type: "text" },
      { key: "review_2_rating", label: "Recension 2 – stjärnor (1-5)", type: "number" },
      { key: "review_2_text", label: "Recension 2 – text", type: "textarea" },
      { key: "review_3_name", label: "Recension 3 – namn", type: "text" },
      { key: "review_3_rating", label: "Recension 3 – stjärnor (1-5)", type: "number" },
      { key: "review_3_text", label: "Recension 3 – text", type: "textarea" },
    ],
  },
  {
    id: "order",
    title: "Beställning-sektion",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title", label: "Rubrik", type: "text" },
      { key: "description", label: "Beskrivning", type: "textarea" },
      { key: "card_1_title", label: "Kort 1 – rubrik", type: "text" },
      { key: "card_1_desc", label: "Kort 1 – text", type: "text" },
      { key: "card_2_title", label: "Kort 2 – rubrik", type: "text" },
      { key: "card_2_desc", label: "Kort 2 – text", type: "text" },
      { key: "card_3_title", label: "Kort 3 – rubrik", type: "text" },
      { key: "card_3_desc", label: "Kort 3 – text", type: "text" },
      { key: "cta_primary", label: "Knapp 1", type: "text" },
      { key: "cta_secondary", label: "Knapp 2", type: "text" },
    ],
  },
  {
    id: "hours",
    title: "Öppettider",
    description: "Visas som egen sektion under Dagens lunch och även i Kontakt-blocket.",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title", label: "Rubrik", type: "text" },
      { key: "subtitle", label: "Underrubrik", type: "textarea" },
      { key: "weekdays_days", label: "Vardagar – etikett", type: "text" },
      { key: "weekdays_time", label: "Vardagar – tider", type: "text" },
      { key: "friday_days", label: "Fredag – etikett", type: "text" },
      { key: "friday_time", label: "Fredag – tider", type: "text" },
      { key: "saturday_days", label: "Lördag – etikett", type: "text" },
      { key: "saturday_time", label: "Lördag – tider", type: "text" },
      { key: "sunday_days", label: "Söndag – etikett", type: "text" },
      { key: "sunday_time", label: "Söndag – tider", type: "text" },
      { key: "note", label: "Extra anteckning (valfri)", type: "textarea" },
    ],
  },
  {
    id: "contact",
    title: "Kontakt",
    description: "Adress, telefon, öppettider.",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title_line1", label: "Rubrik rad 1", type: "text" },
      { key: "title_italic", label: "Rubrik rad 2 (kursiv)", type: "text" },
      { key: "address", label: "Adress", type: "text" },
      { key: "phone", label: "Telefon (visning)", type: "text" },
      { key: "phone_tel", label: "Telefon (tel-format, t.ex. +4666051130)", type: "text" },
      { key: "hours_days", label: "Dagar", type: "text" },
      { key: "hours_time", label: "Tider", type: "text" },
      { key: "maps_query", label: "Google Maps sökfråga", type: "text" },
      { key: "cta_text", label: "Knapp-text", type: "text" },
    ],
  },
  {
    id: "footer",
    title: "Footer",
    fields: [
      { key: "tagline", label: "Tagline", type: "textarea" },
      { key: "instagram_url", label: "Instagram URL", type: "text" },
      { key: "facebook_url", label: "Facebook URL", type: "text" },
      { key: "payment_label", label: "Betalning-etikett", type: "text" },
    ],
  },
  {
    id: "sticky_cta",
    title: "Mobilknapp (sticky)",
    description: "Knappen längst ner på mobil.",
    fields: [{ key: "text", label: "Knappens text", type: "text" }],
  },
  {
    id: "daily_special_section",
    title: "Dagens förslag (rubrik)",
    description: "Text som visas ovanför den handplockade/slumpade maträtten.",
    fields: [
      { key: "eyebrow", label: "Överrubrik", type: "text" },
      { key: "title", label: "Rubrik", type: "text" },
      { key: "subtitle", label: "Underrubrik", type: "textarea" },
    ],
  },
  {
    id: "nav",
    title: "Meny-länkar",
    fields: [
      { key: "menu_label", label: "Meny", type: "text" },
      { key: "about_label", label: "Om oss", type: "text" },
      { key: "reviews_label", label: "Recensioner", type: "text" },
      { key: "contact_label", label: "Kontakt", type: "text" },
    ],
  },
];

function SectionCard({ section, values, onSave }) {
  const [form, setForm] = useState(values || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(values || {});
    setDirty(false);
  }, [values]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // normalize number fields
      const payload = { ...form };
      section.fields.forEach((f) => {
        if (f.type === "number" && payload[f.key] !== "" && payload[f.key] !== undefined) {
          payload[f.key] = Number(payload[f.key]);
        }
      });
      await onSave(section.id, payload);
      toast.success(`${section.title} sparad`);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[var(--border)] rounded-2xl" data-testid={`site-section-${section.id}`}>
      <div className="p-6 border-b border-[var(--border)]">
        <h3 className="font-serif-display text-2xl font-bold">{section.title}</h3>
        {section.description && <p className="text-sm text-[var(--text-muted)] mt-1">{section.description}</p>}
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        {section.fields.map((f) => {
          if (f.type === "image") {
            return (
              <div key={f.key} className="md:col-span-2">
                <ImageField
                  label={f.label}
                  value={form[f.key] || ""}
                  onChange={(v) => update(f.key, v)}
                  testId={`site-${section.id}-${f.key}`}
                />
              </div>
            );
          }
          if (f.type === "textarea") {
            return (
              <label key={f.key} className="block md:col-span-2">
                <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">{f.label}</span>
                <textarea
                  rows={3}
                  data-testid={`site-${section.id}-${f.key}`}
                  value={form[f.key] ?? ""}
                  onChange={(e) => update(f.key, e.target.value)}
                  className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
                />
              </label>
            );
          }
          return (
            <label key={f.key} className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">{f.label}</span>
              <input
                type={f.type === "number" ? "number" : "text"}
                data-testid={`site-${section.id}-${f.key}`}
                value={form[f.key] ?? ""}
                onChange={(e) => update(f.key, e.target.value)}
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </label>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-3 p-4 bg-[var(--surface-alt)] border-t border-[var(--border)] rounded-b-2xl">
        <div className="text-xs text-[var(--text-muted)]">
          {dirty ? "Osparade ändringar" : "Synkat"}
        </div>
        <button
          type="submit"
          disabled={saving || !dirty}
          data-testid={`site-${section.id}-save`}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-50"
        >
          <Save size={15} /> {saving ? "Sparar…" : "Spara"}
        </button>
      </div>
    </form>
  );
}

export default function SiteEditor() {
  const { site, save, reload } = useSite();
  const [openSection, setOpenSection] = useState("hero");

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, []);

  if (!site) return <div className="py-12 text-center text-[var(--text-muted)]">Laddar innehåll…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-black">Webbplatsens innehåll</h1>
        <p className="text-[var(--text-muted)] mt-1">
          Redigera rubriker, texter, bilder och kontaktuppgifter. Ändringar syns direkt på hemsidan.
        </p>
      </div>

      <FeatureToggles />

      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setOpenSection(s.id)}
            data-testid={`site-tab-${s.id}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              openSection === s.id
                ? "bg-[var(--text)] text-white"
                : "bg-white border border-[var(--border)] hover:border-[var(--text)]"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      {SECTIONS.filter((s) => s.id === openSection).map((s) => (
        <SectionCard key={s.id} section={s} values={site[s.id] || {}} onSave={save} />
      ))}
    </div>
  );
}
